import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2 } from "lucide-react";
import { formatPrice } from "@/lib/numberFormat";

interface SubscriptionPlan {
  _id: string;
  name: string;
  priceMonthly: string;
  priceYearly: string | null;
  annualDiscountPercent: number;
  currency: string;
  features: string[];
  isActive: boolean;
  isComingSoon?: boolean;
  sortOrder: number;
}

interface PlanTableProps {
  plans: SubscriptionPlan[];
  onEdit: (plan: SubscriptionPlan) => void;
  onDelete: (id: string) => void;
  onToggleActive?: (id: string, isActive: boolean) => void;
  onToggleComingSoon?: (id: string, isComingSoon: boolean) => void;
}

export function PlanTable({ plans, onEdit, onDelete, onToggleActive, onToggleComingSoon }: PlanTableProps) {
  const sortedPlans = [...plans].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Order</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Daily Price</TableHead>
          <TableHead>Monthly Price</TableHead>
          <TableHead>Yearly Price</TableHead>
          <TableHead>Discount</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Coming Soon</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedPlans.map((plan) => (
          <TableRow key={plan._id}>
            <TableCell>{plan.sortOrder}</TableCell>
            <TableCell className="font-medium">{plan.name}</TableCell>
            <TableCell>{(plan as any).priceDaily ? `${plan.currency} ${formatPrice((plan as any).priceDaily)}` : "-"}</TableCell>
            <TableCell>{plan.currency} {formatPrice(plan.priceMonthly)}</TableCell>
            <TableCell>{plan.priceYearly ? `${plan.currency} ${formatPrice(plan.priceYearly)}` : "-"}</TableCell>
            <TableCell>{plan.annualDiscountPercent}%</TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Switch
                  checked={plan.isActive}
                  onCheckedChange={(checked) => onToggleActive?.(plan._id, checked)}
                />
                <Badge variant={plan.isActive ? "default" : "secondary"}>
                  {plan.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
            </TableCell>
            <TableCell>
              <Switch
                checked={!!plan.isComingSoon}
                onCheckedChange={(checked) => onToggleComingSoon?.(plan._id, checked)}
              />
            </TableCell>
            <TableCell className="text-right">
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="icon" onClick={() => onEdit(plan)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => onDelete(plan._id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
